import _ from "lodash";
import { useEffect, useState } from "react";
import * as yup from "yup";
import {
    Button,
    TransitionChevron,
    FormFieldSelect,
    Text,
    Span
} from "@netapp/bxp-design-system-react";
import { apiClient } from "../../utils/clients";
import { DeleteIcon } from "@netapp/bxp-style/react-icons/Action";
import styles from "./components/Settings.module.scss";
import { ProjectIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { CompanyIcon, FolderIcon } from "@netapp/bxp-style/react-icons/General"
import { UserIcon } from "@netapp/bxp-style/react-icons/Action";
import { ServiceAccountIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import externals from "utils/externals";
import { SuccessIcon, ErrorIcon, NoticeIcon, NoticeTriangleIcon } from "@netapp/bxp-style/react-icons/Notification";
import { Trans } from "react-i18next";
import { identityAccess as i18nNamespace } from "../../i18n/namespace";
import { getScopeType } from "utils/projectToFleet";

export const emailRegex = /^(?=.{0,255}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,20})$/;
export const serviceAccountNameRegex = /^[-_.a-zA-Z0-9]{1,40}$/;
export const GRID_PAGESIZE = 50;
export const USERS_GRID_THRESHOLD = 300;
export const org = "organization";
export const project = "project";
export const ENTRAID = "ENTRAID";
export const PINGFEDERATE = "PINGFEDERATE";
export const ADFS = "ADFS";
export const SAML = "SAML";
export const SOURCE_INITIATED = "SOURCE_INITIATED";
export const AWAITING_APPROVAL = "AWAITING_APPROVAL";
export const DESTINATION_INITIATED = "DESTINATION_INITIATED";
export const APPROVED = "APPROVED";
export const REJECTED = "REJECTED";
export const DISABLED = "DISABLED";
export const HOLD = "HOLD";
export const FEDERATED_GRID_THRESHOLD = 50;
const rsaExpirationMonths = 13;
export const USER_GROUP_EXTENDED_ACCEPT_HEADER = { Accept: "application/vnd.netapp.bxp.usergroups.extended+json" };
export const USER_EXTENDED_ACCEPT_HEADER = { Accept: "application/vnd.netapp.bxp.users.extended+json" };

export const extractLdapHost = (connectionUrl, t) => {
    if (!connectionUrl) return t("n_a");
    try {
        const url = new URL(connectionUrl);
        return url.host;
    } catch (error) {
        const match = connectionUrl.match(/\/\/([^/]+)/);
        return match ? match[1] : connectionUrl;
    }
};

export const getInitialADFormState = () => ({
    name: '',
    ldapHost: '',
    port: '636',
    connectionTimeout: '1000',
    useSSL: true,
    bindDn: '',
    bindCredential: '',
    usersDn: ''
});

export const getADValidationSchema = (t) => yup.object().shape({
    name: yup.string().required(t('name_required')),
    ldapHost: yup.string().required(t('ldap_host_required')),
    bindDn: yup.string().required(t('bind_dn_required')),
    bindCredential: yup.string().required(t('bind_credential_required')),
    usersDn: yup.string()
        .required(t('users_dn_required'))
        .test('no-spaces', t('users_dn_invalid_spaces'), value => {
            if (!value) return true;
            return !/\s/.test(value);
        })
});

export const SCOPE_ROLE_MAP = {
    "project": "internal:bxp:isProjectRole",
    "organization": "internal:bxp:isOrganizationRole",
    "folder": "internal:bxp:isFolderRole"
}

export const FEDERATIONSTATEMAP = t => ({
    "DRAFT": { label: t("draft"), iconType: "info" },
    "CREATED": { label: t("created"), iconType: "info" },
    "TESTED": { label: t("tested"), iconType: "info" },
    "ENABLED": { label: t("enabled"), iconType: "success" },
    "DISABLED": { label: t("disabled"), iconType: "error" },
    "FAILED": { label: t("failed"), iconType: "error" }
})

export const FEDERATIONPROVIDERMAP = {
    ADFS: {
        title: "AD FS",
        key: "adfs",
        label: 'Active Directory Federation Services (AD FS)',
        type: "PROTOCOL",
    },
    SAML: {
        title: "SAML",
        key: "saml",
        label: 'SAML identity provider',
        type: "PROTOCOL",
    },
    ENTRAID: {
        title: "Microsoft Entra ID",
        key: "entraId",
        label: 'Microsoft Entra ID',
        type: "PROVIDER",
    },
    PINGFEDERATE: {
        title: "PingFederate",
        key: "pingFederate",
        label: 'Ping Federation',
        type: "PROVIDER",
    }
}

export const PARTNERSHIP_TYPE_MAP = {
    "Self-Service": "Self service",
    "msp": "Managed service provider",
    "reseller": "Reseller",
    "customer_support": "Customer support",
}

export const supportedPartnershipsRoles = ["Self-Service"];

export const PARTNERSHIP_STATUS = {
    SOURCE_INITIATED: { label: "initiated", iconType: "info", Icon: NoticeIcon },
    AWAITING_APPROVAL: { label: "awaiting_approval", iconType: "warning", Icon: NoticeTriangleIcon },
    APPROVED: { label: "established", iconType: "success", Icon: SuccessIcon },
    REJECTED: { label: "rejected", iconType: "error", Icon: ErrorIcon },
    DESTINATION_INITIATED: { label: "destination_initiated", iconType: "warning", Icon: NoticeIcon },
    HOLD: { label: "disabled", iconType: "info", Icon: NoticeIcon },
}

export const PARTNERSHIP_OPERATIONS = {
    APPROVE: "APPROVE",
    REJECT: "REJECT",
    ENABLE: "ENABLE",
    DISABLE: "DISABLE",
    ADD_MEMBER: "ADD_MEMBER",
    CANCEL_REQUEST: "CANCEL_REQUEST",
    DELETE_PARTNERSHIP: "DELETE_PARTNERSHIP"
}

export const PARTNERSHIP_GRID_TABS = ['INITIATED', 'RECEIVED'];

export const getPaginationQuery = (
    pageSize,
    sortState,
    filterState,
    pageIndex,
    defaultFilters = null
) => {
    let query = {};
    if (pageSize) {
        query = {
            limit: pageSize,
            skip: pageSize * pageIndex,
        };
    }
    if (sortState && sortState.column && sortState.sortOrder) {
        query.order_by = `${sortState.column}${sortState.sortOrder === ' desc' ? sortState.sortOrder : ''}`;
    }
    if (defaultFilters) {
        query.filter = defaultFilters;
    }
    return query;
};

export const isOrganizationAdmin = (role) => {
    return !_.isUndefined(role?.rules) && role.rules.includes("bxp:organization-create")
        && role.rules.includes("bxp:organization-modify")
        && role.rules.includes("bxp:organization-delete")
}

export const isAdmin = (role) => {
    return !_.isUndefined(role?.rules) && role.rules.includes("bxp:organization-create")
        && role.rules.includes("bxp:organization-modify")
        && role.rules.includes("bxp:organization-delete")
        && role.rules.includes("bxp:folder-create")
        && role.rules.includes("bxp:folder-modify")
        && role.rules.includes("bxp:folder-delete")
        && role.rules.includes("bxp:project-create")
        && role.rules.includes("bxp:project-modify")
        && role.rules.includes("bxp:project-delete")
}

export const isScopeAdmin = (role) => {
    return !_.isUndefined(role?.rules) && !isOrganizationAdmin(role)
        && role.rules.includes("bxp:folder-create")
        && role.rules.includes("bxp:folder-modify")
        && role.rules.includes("bxp:folder-delete")
        && role.rules.includes("bxp:project-create")
        && role.rules.includes("bxp:project-modify")
        && role.rules.includes("bxp:project-delete")
}

export const isPlatformViewer = (role) => {
    return getTagValue(role.tags, "internal:bxp:legacyId") === "Role-3"
}

export const isSnapcenterAdmin = (role) => {
    return getTagValue(role.tags, "internal:bxp:legacyId") === "Role-4"
}

export const isSnapcenterSystem = (role) => {
    return getTagValue(role.tags, "internal:bxp:legacyId") === "Role-5"
}

export function useAPIQuery(urlObject) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(urlObject?.url ? true : false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!_.isEmpty(urlObject?.url)) {
            (async () => {
                setIsLoading(true);
                setError(null);
                try {
                    let response;
                    if (urlObject.query) {
                        response = await apiClient.get(urlObject.url, { params: urlObject?.query });
                    } else {
                        response = await apiClient.get(urlObject.url);
                    }
                    const data = JSON.parse(response.data);
                    setData(data);
                }
                catch (err) {
                    setError(err);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [urlObject]);
    return { data, isLoading, error }
}

export const getFoldersAndProjectsMap = (resources) => {
    const foldersAndProjectsMap = {
        folders: [],
        projects: []
    };
    _.forEach(resources, function (resource) {

        if (!_.isEmpty(resource["childScopes"])) {
            const childScopesFoldersAndProjectsMap = getFoldersAndProjectsMap(resource["childScopes"]);
            foldersAndProjectsMap.folders = [...foldersAndProjectsMap.folders, ...childScopesFoldersAndProjectsMap.folders];
            foldersAndProjectsMap.projects = [...foldersAndProjectsMap.projects, ...childScopesFoldersAndProjectsMap.projects];
        }
        if (resource.resourceType === "folder") {
            foldersAndProjectsMap.folders.push(resource);
        }

        if (resource.resourceType === "project") {
            foldersAndProjectsMap.projects.push(resource);
        }
    });
    return foldersAndProjectsMap;
}

export const isScopeRoleValid = (scope, role) => {
    if (scope?.resourceType === org && isScopeAdmin(role)) {
        return false;
    }
    if (scope?.resourceType !== org && isOrganizationAdmin(role)) {
        return false;
    }
    return true;
}

/* This function is used to find the scopes for a resource.
 * @param {Object} selectedOrg - selectedOrg data
 * @param {String} scopeId - Id of the resource
 * output - Array of scopes for the resource. Ex: ["gil", "ws1"]  Ex2:  ["gil", "ws2", "test"] }]
 */
export const findPathToScopeRoles = (scopes, scopeId, roleMap, scopeAccessMap) => {
    if (!scopes || scopes?.length === 0) {
        return [];
    }
    for (let child of scopes) {
        if (child.id === scopeId) {
            let multiRoles = [];
            let isParnershipRole = false;
            if (scopeAccessMap[child.id]) {
                multiRoles = scopeAccessMap[child.id].roles.map(role => roleMap[role.id]);
                isParnershipRole = scopeAccessMap[child.id].roles.map(role => !!role.partnershipId)?.[0];
            }
            return multiRoles.length > 0 && [{ id: child.id, name: child.name, rules: child.rules, type: child.resourceType, multiRoles: _.cloneDeep(multiRoles), isParnershipRole }];
        } else {
            const result = findPathToScopeRoles(child.childScopes, scopeId, roleMap, scopeAccessMap);
            if (result.length > 0) {
                const associatedRoles = result[result.length - 1].multiRoles
                let allInheritedRoles = false;
                let isParnershipRole = false;
                if (scopeAccessMap[child.id]) {
                    // check if roles are inherited
                    const scopeRoles = scopeAccessMap[child.id].roles.map(role => role.id);
                    isParnershipRole = scopeAccessMap[child.id].roles.map(role => !!role.isParnershipRole)?.[0];
                    allInheritedRoles = true
                    for (const role of associatedRoles) {
                        if (scopeRoles.includes(role.id)) {
                            role.isInherited = true;
                        } else {
                            allInheritedRoles = false
                        }
                    }
                }
                return result.length === 1 && allInheritedRoles ? [] : [{ id: child.id, name: child.name, rules: child.rules, type: child.resourceType, isParnershipRole }, ...result];
            }
        }
    }
    return [];
};

export const findPathToScope = (scope, scopeId) => {
    if (!scope) {
        return [];
    }
    if (scope.id === scopeId) {
        return [{ id: scope.id, name: scope.name, rules: scope.rules, type: scope.resourceType }];
    }
    for (let child of scope.childScopes) {
        const result = findPathToScope(child, scopeId);
        if (result.length > 0) {
            return [{ id: scope.id, name: scope.name, rules: scope.rules, type: scope.resourceType }, ...result];
        }
    }
    return [];
};

// Get existing accessible scope IDs
export const getScopeIds = (selectedUser, allRoles, orgId) => {
    const orgAdminRoleId = allRoles.find(r => isOrganizationAdmin(r))?.id
    if (selectedUser?.roles?.find(id => id === orgAdminRoleId)) {
        const scopeIdMap = {};
        scopeIdMap[orgId] = true;
        return scopeIdMap;
    } else {
        // This list is prepared to disable access to already accessible scopes, 
        // hence platform viewer's org access is skipped as its just a read access
        return selectedUser?.roles?.length > 0 && _.reduce(selectedUser.tags,
            (acc, tag) => {
                const key = Object.keys(tag)[0];
                if (key === "internal:bxp:projectId" || key === "internal:bxp:folderId") {
                    acc[tag[key]] = true
                }
                return acc;
            },
            {}
        );
    }
}

export const countResourceTypes = (userScopes, organization) => {
    if (!userScopes || userScopes.length === 0) {
        return { folderCount: 0, projectCount: 0 };
    } else {
        const userScopesSorted = _.sortBy(userScopes, (scope) => scope.length)
        const scopeWithAccess = {};
        for (const userScope of userScopesSorted) {
            if (Object.keys(scopeWithAccess).length === 0) {
                scopeWithAccess[userScope[`${userScope.type}Id`]] = userScope[`${userScope.type}Id`]
            } else {
                let hasParentAccess = false
                for (const scope of userScope.scopeDetails) {
                    if (scopeWithAccess[scope.id]) {
                        hasParentAccess = true
                        break
                    }
                }
                if (!hasParentAccess) {
                    scopeWithAccess[userScope[`${userScope.type}Id`]] = userScope[`${userScope.type}Id`]
                }
            }
        }
        // Get Resource Count
        const count = { folderCount: 0, projectCount: 0 }
        const scopes = [organization]
        while (scopes.length > 0) {
            const scope = scopes[0]
            if (scopeWithAccess[scope.id]) {
                if (scope.resourceType === 'folder') {
                    count.folderCount += 1
                }
                if (scope.resourceType === 'project') {
                    count.projectCount += 1
                }
            }
            if (scope.childScopes?.length > 0) {
                for (const childScope of scope.childScopes) {
                    if (scopeWithAccess[scope.id]) {
                        scopeWithAccess[childScope.id] = childScope.id
                    }
                    scopes.push(childScope)
                }
            }
            scopes.shift()
        }
        return count
    }
}

export const scopeTypeIconRenderer = (element) => {
    if (element.resourceType === "organization") {
        return <CompanyIcon className={element?.isSelected ? "" : styles["dark-icon"]} width="20" />
    } else if (element.resourceType === "folder") {
        return <FolderIcon className={element?.isSelected ? "" : styles["dark-icon"]} width="20" />
    } else if (element.resourceType === "project") {
        return <ProjectIcon className={element?.isSelected ? "" : styles["dark-icon"]} width="20" />
    }
    return null
}

export const getOrgColumnsHeaders = (t) => [
    {
        header: t('name'),
        accessor: "name",
        id: "name",
        sort: {
            enabled: true,
        },
        Renderer: ({ row }) => {
            return row.resourceType === "organization" ? (
                <div className={styles["colum-cell-treegrid"]}>
                    <CompanyIcon width="20" className={styles["grid-icon"]}></CompanyIcon>
                    <Text
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                            wordBreak: "break-word"
                        }}
                    >
                        {row.name}
                    </Text>
                </div>
            ) : row.resourceType === "folder" ? (
                <div className={styles["colum-cell-treegrid"]}>
                    <FolderIcon width="20" className={styles["grid-icon"]}></FolderIcon>
                    <Text
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                            wordBreak: "break-word",
                        }}
                    >
                        {row.name}
                    </Text>
                </div>
            ) : (
                <div className={styles["colum-cell-treegrid"]}>
                    <ProjectIcon width="20" className={styles["grid-icon"]}></ProjectIcon>
                    <Text
                        style={{
                            marginLeft: "10px",
                            verticalAlign: "super",
                            wordBreak: "break-word"
                        }}
                    >
                        {row.name}
                    </Text>
                </div>
            );
        },
    },
];
export const RESOURCES_SELECTABLE_MAP = t => {
    return {
        Folder: {
            label: t("Folder"),
            description:
                t(getScopeType("folder_description_project")),
            Icon: FolderIcon,
        },
        Project: {
            label: t(getScopeType("Project")),
            description: t(getScopeType("project_description")),
            Icon: ProjectIcon,
        }
    }
};

// Validation for ResourceName and Location
export const validator = (isOrganization, t) =>
    yup.object().shape({
        name: yup
            .string()
            .required(t("field_required"))
            .matches(
                /^.{1,300}$/,
                t("name_length"),
            ),
        parent: !isOrganization
            ? yup.array().min(1).required(t("field_required"))
            : null,
    });

export const createFilterExpression = (externalfilter, optionString) => {
    if (!externalfilter) {
        externalfilter = `${optionString}`
    } else {
        externalfilter = `${externalfilter} or ${optionString}`
    }
    return externalfilter
}

export const getSelectedResources = (selectedRows) => {
    const selectedResources = [];
    for (const selectedRow of Object.keys(selectedRows)) {
        if (selectedRows[selectedRow]) {
            selectedResources.push(selectedRow);
        }
    }
    return selectedResources;
}

export const recursiveSearch = (recursiveObjectKey, parentData, searchString, isParentMatched = false) => {
    /**
     * recursiveObjectKey: is the key in the object that you want do the recursive search on.
     * parentData: is the main object that you pass to filter out the search.
     * searchString: is the text that you want to search.
     * !!! isParentMatched: No need to pass this parameter, it is being set and passed internally.
     */
    let matchedChildNodes = []
    _.forEach(parentData, childNode => {
        const childNodeMatched = _.toLower(childNode.name).includes(_.toLower(searchString));
        if (childNode.resourceType === "organization" && childNodeMatched) {
            matchedChildNodes = parentData;
            return;
        }
        let childNodesResults = [];
        if (childNode[recursiveObjectKey]?.length > 0) {
            childNodesResults = recursiveSearch(recursiveObjectKey, childNode[recursiveObjectKey], searchString, childNodeMatched);
        }
        if (childNodeMatched || childNodesResults.length > 0) {
            const newNode = { ...childNode };
            if (childNodesResults.length > 0) {
                newNode[recursiveObjectKey] = childNodesResults;
            }
            matchedChildNodes.push(newNode);
        }
    });
    return matchedChildNodes;
}

export const getTableColumns = ({ dropdownUsers, onSelectUser, deleteRow, addMultiRoleHandler, t }) => {
    return [
        {
            header: t("type"),
            accessor: "userType",
            id: "userType",
            width: 100,
            fixedWidth: true,
            sort: {
                enabled: false
            },
            filter: {
                filter: (value, row, filterState) => {
                    if (filterState.values.serviceAccount && (row.userType === 'serviceAccount' || row.userType === 'agent')) {
                        return true;
                    }
                    if (filterState.values.user && row.userType === 'user') {
                        return true;
                    }
                },
                enabled: true
            },
            Renderer: (user) => {
                const { row: { userType } } = user;
                return (
                    <div className={styles["grid-icon"]}>
                        {userType === "user" && <UserIcon />}
                        {userType === "serviceAccount" && <ServiceAccountIcon />}
                    </div>
                );
            }
        },
        {
            header: t("name"),
            accessor: "name",
            id: "name",
            width: 300,
            sort: {
                enabled: false
            },
            filter: {},
            Renderer: ({ row }) => {
                return (
                    <>
                        {row.name ? (
                            row.name
                        ) : (
                            <div onClick={(e) => e.stopPropagation()} className={styles["w-full"]}>
                                <FormFieldSelect
                                    options={dropdownUsers}
                                    isSearchable
                                    value={dropdownUsers?.find((user) => user.name === row.name)}
                                    className={styles["user-select-dropdown"]}
                                    onChange={(e) =>
                                        onSelectUser(e, row.id, "name")
                                    }
                                    placeholder={t("select_user")}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                    </>
                );
            },
        },
        {
            header: t("role"),
            accessor: "role",
            id: "role",
            width: 300,
            sort: {
                enabled: false
            },
            Renderer: ({ row }) => {
                const userRoles = row.userRoles;
                let lengthCount = 0;
                let maxIndex = 0;
                return (
                    <>
                        {!_.size(userRoles) ? (
                            <Button onClick={(e) => addMultiRoleHandler(e, row)} variant="text">
                                {t("assign_role")}
                            </Button>
                        ) : userRoles.length === 1 ? (
                            userRoles[0].name
                        ) : (
                            <>
                              <div>
                                {userRoles.map((role, index) => {
                                    if (lengthCount <= 25) {
                                        maxIndex = index + 1;
                                        lengthCount = lengthCount + role.name.length;
                                        return (
                                            <div key={index}>
                                                {" "}{role.name}{lengthCount <= 25 && ";"}{" "}
                                            </div>
                                        );
                                    }
                                })}
                               </div>
                                {maxIndex !== userRoles.length && (
                                    <Span color="info">+{userRoles.length - maxIndex}</Span>
                                )}
                            </>
                        )}
                    </>

                );
            }
        },
        {
            header: "",
            accessor: "",
            width: 90,
            sort: {
                enabled: false,
            },
            filter: {
                enabled: false,
            },
            Renderer: ({ row, rowState }) => {
                return (
                    <div className={styles["delete-row-action-menu"]}>
                        <TransitionChevron isActive={rowState.isExpanded} color="text" thin wide />
                        {row.deletableColumn && <Button
                            variant="icon"
                            color="secondary"
                            style={{ width: 20, height: 18 }}
                            onClick={() => deleteRow(row)}>
                            <DeleteIcon />
                        </Button>}
                    </div>
                );
            },
        },
    ];
}

export const getUsersSummary = (users, t) => {
    let str = "";
    const roleMap = new Map();
    _.forEach(users, (user) => {
        _.forEach(user.userRoles, (role) => {
            if (!roleMap.has(role.name)) {
                roleMap.set(role.name, 1);
            } else {
                let count = roleMap.get(role.name);
                count++;
                roleMap.set(role.name, count);
            }
        })
    })

    roleMap.forEach((value, key) => {
        if (!str.length) {
            str = `${value} ${key} `
        } else {
            str = `${str} | ${value} ${key}`
        }
    });
    return str ? str : t("action_needed");
}

// TODO this logic need to be changed for Multi - Role support.
export const getRoleWithHigherAccess = (roles) => {
    let selectedRole;
    _.forEach([isOrganizationAdmin, isScopeAdmin, isPlatformViewer, isSnapcenterAdmin, isSnapcenterSystem], (isRoleType) => {
        const selectedUserRole = roles.find(role => isRoleType(role));
        if (!_.isEmpty(selectedUserRole)) {
            selectedRole = selectedUserRole;
            return false;
        }
    });
    return _.isUndefined(selectedRole) ? roles[0] : selectedRole
}

export const getBoolean = (value) => {
    if (_.isUndefined(value) || (value !== "true" && value !== "false")) {
        return false;
    }
    return value === "true" ? true : false;
}

export const getTagValue = (tags, key) => {
    const tag = _.find(tags, tag => !_.isUndefined(tag[key]))
    return tag ? tag[key] : undefined;
}

export const getRoleName = (allRoles, roleIds) => {
    const selectedUserRoles = _.map(roleIds, roleId => {
        const selectedUserRole = allRoles.find(userRole => userRole.id === roleId);
        return selectedUserRole ? { ...selectedUserRole } : undefined;
    }).filter(Boolean); 
    return selectedUserRoles.length ? getRoleWithHigherAccess(selectedUserRoles) : undefined;
};

export const getRolesByIds = (allRoles, roleIds, nonEditableRoles, isEdit) => {
    return _.map(roleIds, function (roleId) {
        const selectedUserRole = allRoles.find(userRole => userRole.id === roleId);
        if (!selectedUserRole) return null;
        const isModifyAllowed = !_.includes(nonEditableRoles || [], roleId) && isEdit;
        selectedUserRole.deletableColumn = isModifyAllowed
        selectedUserRole.editableColumn = isModifyAllowed
        return selectedUserRole ? { ...selectedUserRole } : undefined;
    }).filter(Boolean);
};

export const mergeArray = (array1, array2, property) => {
    const mergedData = {};
    for (const item of array1) {
        mergedData[item[property]] = item;
    }
    for (const item of array2) {
        mergedData[item[property]] = item;
    }
    return Object.values(mergedData);
}

export const getRoleId = (allRoles, roleId) => {
    const selectedRole = _.find(allRoles, function (role) {
        return role.id === roleId
    })
    return selectedRole;
};

export const hasAncestor = (ancestors, ancestorMap) => {
    for (const ancestor of ancestors) {
        if (ancestorMap[ancestor]) {
            return true
        }
    }
    return false
}

export const createAgentRoleAPIList = (rolesMapped) => {
    const apiList = [];
    for (const { scope, ancestors } of rolesMapped) {
        _.forEach(scope, eachScope => apiList.push(`${externals.tenancyV4Backend}/${eachScope.resourceType}s/${eachScope.id}/users`))
    }
    return apiList;
}

export const createFederatedGroupRoleAPIList = (rolesMapped) => {
    return createRoleAPIListBase(rolesMapped, ({ role, scope }) =>
        `${externals.tenancyV4Backend}/${scope.resourceType}s/${scope.id}/roles/${role.value}/usergroups`
    );
};

export const createRoleAPIList = (rolesMapped) => {
    return createRoleAPIListBase(rolesMapped, ({ role, scope }) =>
        `${externals.tenancyV4Backend}/${scope.resourceType}s/${scope.id}/roles/${role.value}/users`
    );
};

const createRoleAPIListBase = (rolesMapped, buildUrlFn) => {
    if (rolesMapped.length === 1) {
        return [buildUrlFn(rolesMapped[0])];
    }
    rolesMapped = rolesMapped.sort((from, to) => {
        const fromAncestors = from.scope.resourceType === "organization" ? [] : getTagValue(from.scope.tags, 'internal:bxp:ancestors').split(',');
        const toAncestors = to.scope.resourceType === "organization" ? [] : getTagValue(to.scope.tags, 'internal:bxp:ancestors').split(',');
        from['ancestors'] = fromAncestors;
        to['ancestors'] = toAncestors;
        return fromAncestors.length - toAncestors.length;
    });
    const parentScopeWithAdmin = {};
    const apiList = [];
    for (const { role, scope, ancestors } of rolesMapped) {
        if (isOrganizationAdmin(role)) {
            apiList.push(buildUrlFn({ role, scope }));
            break;
        } else if (!hasAncestor(ancestors, parentScopeWithAdmin)) {
            if (isScopeAdmin(role)) {
                parentScopeWithAdmin[scope.id] = scope;
            }
            apiList.push(buildUrlFn({ role, scope }));
        }
    }
    return apiList;
};

export const filteredTreeResults = (filterKey, optionArray) => {
    if (!_.isUndefined(filterKey) && filterKey.length > 2 && filterKey !== '') {
        const lowerCasedFilter = _.toLower(filterKey);

        const filteredData = optionArray[0].childScopes?.length ?
            recursiveSearch('childScopes', [optionArray[0]], lowerCasedFilter) : [];
        return filteredData.length ? filteredData : {};
    }
    return optionArray;
}

export const getUserWithAccess = (user, isEdit, isResourceTypeOrg, allRoles, nonEditableRoles, currentLoginUser) => {
    const role = getRoleName(allRoles, user.roles);
    const userRoles = getRolesByIds(allRoles, user.roles, nonEditableRoles, isEdit);
    user.gridRoleName = role?.name || '';
    user.gridRoleId = role?.id || '';
    user.userRoles = userRoles;
    if (isResourceTypeOrg) {
        user.deletableColumn = false;
        user.editableColumn = false;
    } else if (isEdit && nonEditableRoles.length == 0) {
        user.deletableColumn = user.id !== currentLoginUser;
        user.editableColumn = user.id !== currentLoginUser;
    } else {
        user.deletableColumn = false;
        user.editableColumn = user.id !== currentLoginUser
    }
    return user;
}

export const getScopeWithoutAdminAccess = (selectedScope, hasAccess = (rules) => true, scopeIdsWithoutAccess = []) => {
    if (!hasAccess(selectedScope.rules)) {
        scopeIdsWithoutAccess.push(selectedScope.id);
        for (const scope of selectedScope.childScopes) {
            if (!hasAccess(scope.rules)) {
                getScopeWithoutAdminAccess(scope, hasAccess, scopeIdsWithoutAccess)
            } else {
                break;
            }
        }
    }
    return scopeIdsWithoutAccess;
}

export const hasModifyUserAccociationAccess = (roles) => {
    return roles && roles.includes("bxp:usergroup-create") && roles.includes("bxp:usergroup-delete") && roles.includes("bxp:usergroup-modify")
}

export const mergeUsersInHierarchy = (usersInHierarchy, editedUsers = [], allRoles, currentLoginUser) => {
    const usersWithAccessMap = {};
    const usersWithOutAccessMap = {};
    let index = 0;
    while (index < usersInHierarchy.length) {
        if (!_.isUndefined(usersInHierarchy[index])) {
            for (const user of usersInHierarchy[index]) {
                if (_.isUndefined(user.roles) || user.roles.length === 0) {
                    if (index === 0 || !usersWithAccessMap[user.id] && !usersWithOutAccessMap[user.id]) {
                        usersWithOutAccessMap[user.id] = user
                    }
                } else {
                    if (usersWithAccessMap[user.id]) {
                        // update the user with acess with the user in the hierarchy.
                        usersWithAccessMap[user.id] = getUserWithAccess(user, index === usersInHierarchy.length - 1, false, allRoles, usersWithAccessMap[user.id].roles, currentLoginUser)
                    } else {
                        // add the user to the user with access which delete permisision if user has access to last element in the hierarchy
                        if (index === usersInHierarchy.length - 1 && user.editableColumn) {
                            usersWithAccessMap[user.id] = user;
                        } else {
                            usersWithAccessMap[user.id] = getUserWithAccess(user, index === usersInHierarchy.length - 1, false, allRoles, [], currentLoginUser)
                        }
                    }
                    // remove the users without access
                    if (usersWithOutAccessMap[user.id]) {
                        delete usersWithOutAccessMap[user.id]
                    }
                }
            }
        }
        index++;
    }
    return {
        usersWithAccessList: [...Object.values(usersWithAccessMap)],
        usersWithOutAccessList: Object.values(usersWithOutAccessMap)
    }
}

export const getDeleteResourceMessage = (resourceDetails, t) => {
    return (
        <>
            <Text level={13}>
                <Trans 
                    ns={i18nNamespace}
                    i18nKey="resource_deleted_success"
                    values={{ resourceType: t(getScopeType(_.capitalize(resourceDetails.resourceType)), i18nNamespace), resourceName: resourceDetails.name }}
                    components={[
                        <span className={styles['wrapping-text']} />
                    ]}
                />
            </Text>
        </>
    );
};

export const createRoleObjectById = (roles, allRoles) => {
    return _.map(roles, (roleId) => {
        const role = allRoles.find((role) => role.id === roleId);
        role.label = role.name;
        role.value = role.id;
        return role ? { ...role } : undefined;
    });
};

export const getEntityAndCategorySpecificRoles = (allRoles = [], entity = "", category = "") => {
    if (!entity && !category) return allRoles;
    let filteredRoles = [...allRoles];

    if (entity) {
        const keyMapping = {
            user: "isUserRole",
            serviceAccount: "isServiceAccountRole",
            agent: "isAgentRole"
        }
        filteredRoles = allRoles.filter(role => _.isUndefined(role[keyMapping[entity]]) || role[keyMapping[entity]] === "true")
    }

    if (category) {
        filteredRoles = _.filter(filteredRoles, role => _.find(role.tags, tag => tag["internal:bxp:categoryType"] === category))
    }

    return filteredRoles;
}

export const getRolesFromCategory = (allRoles = [], category, resourceType) => {

    if (_.isUndefined(category) || !_.size(allRoles) || _.isUndefined(resourceType)) {
        return [];
    }

    const filterRole = _.filter(allRoles, role => {
        const isCategory = role.category === category;
        let isValidResourceType = false;
        _.forEach(role.tags, tag => {
            const scopeTypeRole = tag[SCOPE_ROLE_MAP[resourceType]]
            if (!_.isUndefined(scopeTypeRole)) {
                isValidResourceType = JSON.parse(scopeTypeRole);
            }
        });
        return isCategory && isValidResourceType;
    });

    return filterRole
}

export const filterRoleFromTags = (allRoles = [], resourceType) => {

    if (!_.size(allRoles) && _.isUndefined(resourceType)) {
        return [];
    }

    const filterRole = _.filter(allRoles, role => {
        let isValidResourceType = false;
        _.forEach(role.tags, tag => {
            const scopeTypeRole = tag[SCOPE_ROLE_MAP[resourceType]]
            if (!_.isUndefined(scopeTypeRole)) {
                isValidResourceType = JSON.parse(scopeTypeRole);
            }
        });
        return isValidResourceType;
    });

    return filterRole
}

export function getResourcesByRole(roleId, allRoles, resourceList) {
    const matchedRole = _.find(allRoles, { value: roleId });
    if (!matchedRole || !matchedRole.tags) return [];

    const roleTagMap = _.reduce(matchedRole.tags, (accumulator, tag) => {
        const [key, value] = _.toPairs(tag)[0];
        accumulator[key] = value;
        return accumulator;
    }, {});

    return _.map(
        _.filter(resourceList, ({ resourceType }) => roleTagMap[SCOPE_ROLE_MAP[resourceType]] === "true"),
        'id'
    );
}

export const expirationPeriodOptions = [
    { label: "30 days before expiration", value: "P30D" },
    { label: "7 days before expiration", value: "P7D" }
];

export const updateFederationForNonLocalOrDark = async (federationId, organizationId, data) => {
    const updateFederationUrl = `${externals.federationBackend}/organizations/${organizationId}/federations/${federationId}/clients`;
    try {
        const results = await apiClient.post(updateFederationUrl, data);
        if (results && results.data) {
            return Promise.resolve(true);
        }
        return Promise.resolve(false);
    }
    catch (err) {
        return Promise.resolve(false);
    }
}


export const getDomainsAndFederation = async (federationId, selectedOrgId) => {
    const domainUrl = `${externals.federationBackend}/organizations/${selectedOrgId}/domains`;
    const federationUrl = `${externals.federationBackend}/organizations/${selectedOrgId}/federations/${federationId}`;
    try {
        const results = await Promise.allSettled([
            apiClient.get(domainUrl),
            apiClient.get(federationUrl)])
        if (results.length > 0) {
            const domainData = results[0] && results[0].status === "fulfilled" ? results[0].value?.data?.items : [];
            const federationData = results[1] && results[1].status === "fulfilled" ? results[1].value?.data : null;
            return Promise.resolve({
                domains: domainData.filter(domain => domain.state === "VERIFIED").map((domain) => ({ label: domain.name, value: domain.id })),
                federation: federationData
            });
        }
        return Promise.reject({ errorMessage: "Failed to fetch domain and federation data." });
    }
    catch (err) {
        return Promise.reject(err);
    }
}

export const getFederations = async (organizationId) => {
    try {
        const federationUrl = `${externals.federationBackend}/organizations/${organizationId}/federations`;
        const results = await apiClient.get(federationUrl);
        const federationData = results && results.data?.items?.length > 0 ? results.data.items : [];
        return Promise.resolve({
            federation: federationData
        });
    }
    catch (err) {
        return Promise.reject(err);
    }
}

export const getSingleFederation = async (federationId, organizationId) => {
    const federationUrl = `${externals.federationBackend}/organizations/${organizationId}/federations/${federationId}`;
    try {
        const results = await apiClient.get(federationUrl);
        if (results && results.data) {
            return Promise.resolve(results.data);
        } else {
            return Promise.reject({ errorMessage: "Failed to fetch federation data." });
        }
    }
    catch (err) {
        return Promise.reject(err);
    }
}

export const getSingleImportFederation = async () => {
    const importFederationUrl = `${externals.federationBackend}/unmigratedconnections`;
    try {
        const results = await apiClient.get(importFederationUrl);

        if (results && results.data) {
            return Promise.resolve(results.data);
        } else {
            return Promise.reject({ errorMessage: "Failed to fetch for connection to import." });
        }
    }
    catch (err) {
        return Promise.reject(err);
    }
}

export const restructureState = ({ federation }) => {
    let state = {}
    if (federation.state === "TESTED") {
        state = {
            ...state,
            testConnection: {
                status: 'Success'
            }
        }
    }
    if (federation.state === "TESTED" || federation.state === "CREATED") {
        if (federation.provider && federation.provider === "ping") {
            state = {
                ...state,
                createPingConnectionForm: {
                    federateServerUrl: federation?.pingFederateOptions?.serverUrl,
                    "X509 Signing certificate": {
                        contents: federation?.pingFederateOptions?.signingCertificateFingerprint,
                        fileName: "New Text Document.pem",
                        fileSize: 0
                    }
                }

            }
        } else {
            state = {
                ...state,
                createMicrosoftEntraIdConnectionForm: {
                    clientId: federation?.entraIdOptions?.clientId,
                    clientSecret: federation?.entraIdOptions?.clientSecretMasked,
                    microsoftEntraId: federation?.entraIdOptions?.tenantDomain
                }

            }
        }

        if (federation.protocol && federation.protocol === "ADFS") {
            state = {
                ...state,
                createAdfsConnectionForm: {
                    federationUrl: federation?.adfsOptions?.metadataUrl,
                    file: {
                        contents: federation?.adfsOptions?.signingCertificateFingerprint,
                        fileName: "New Text Document.pem",
                        fileSize: 0
                    }
                }
            }
        } else {
            state = {
                ...state,
                createSamlConnectionForm: {
                    signInUrl: federation?.samlOptions?.signInUrl,
                    signOutUrl: federation?.samlOptions?.signOutUrl,
                    "X509 Signing certificate": {
                        contents: federation?.samlOptions?.signingCertificateFingerprint,
                        fileName: "New Text Document.pem",
                        fileSize: 0
                    }
                }

            }
        }

    }
    const { provider, protocol } = FEDERATIONPROVIDERMAP[federation.providerType]?.type?.toLowerCase() === "provider" ? { provider: federation.providerType, undefined } : { undefined, protocol: federation.providerType };
    state = {
        ...state,
        domainDetailsForm: {
            email: Array.isArray(federation.domains) ? null : { domain: federation?.domains },
            federateOption: Array.isArray(federation.domains) ? "other" : state?.domainDetailsForm?.otherDomain?.domains,
            federationName: federation.name,
            otherDomain: Array.isArray(federation.domains) ? { domain: federation.domains } : null,
        },
        selectedProvider: provider,
        selectedProtocol: protocol,
        connectionMethodForm: {
            methodType: FEDERATIONPROVIDERMAP[federation.providerType]?.type?.toLowerCase(),
            protocol: protocol,
            provider: provider
        },
        state: federation.state,
        id: federation.id,
        auth0Name: federation.auth0Name,
        expirationTimestamp: federation.expirationTimestamp
    }
    return state;
}

export const calculateNotificationPeriod = (notificationPeriod) => {
    switch (notificationPeriod?.value) {
        case "P30D":
            return 30;
        case "P7D":
            return 7;
        default:
            return '';
    }
}

export const createImportFederationPayload = (state, organizationId) => {
    const payload = {
        "expirationNotificationPeriod": state.notificationPeriod,
        "name": state.name,
        "organizationId": organizationId,
        "stateDesired": "IMPORTED",
        "type": "application/vnd.netapp.bxp.unmigratedconnection",
        "version": "1.0"
    }
    if (state.expirationPeriodOptions) {
        payload["expirationTimestamp"] = state.expirationPeriodOptions.toISOString();
    }
    return payload;
}

export const createFederationPayload = (state) => {
    const type = state?.connectionMethodForm?.methodType === 'protocol' ? state.connectionMethodForm.protocol : state.connectionMethodForm.provider;
    let payload = {
        domains: state.domainDetailsForm.email ? [state.domainDetailsForm.email.domain] : state.domainDetailsForm.otherDomain.domain,
        expirationNotificationPeriod: state.notificationPeriod,
        expirationTimestamp: state.expirationTimestamp,
        name: state.domainDetailsForm.federationName,
        providerType: type
    }
    switch (type) {
        case ENTRAID: {
            payload = {
                ...payload,
                entraIdOptions: {
                    clientId: state.createMicrosoftEntraIdConnectionForm.clientId,
                    clientSecret: state.createMicrosoftEntraIdConnectionForm.clientSecret,
                    tenantDomain: state.createMicrosoftEntraIdConnectionForm.microsoftEntraId
                }
            }
            break;
        }
        case ADFS: {
            payload = {
                ...payload,
                adfsOptions: state.createAdfsConnectionForm?.metaDataFile?.contents ? {
                    metadataFile: state.createAdfsConnectionForm?.metaDataFile?.contents ?? undefined
                } : {
                    metadataUrl: state.createAdfsConnectionForm.federationUrl ?? undefined,
                }
            }
            break;
        }
        case SAML: {
            payload = {
                ...payload,
                samlOptions: {
                    signingCertificate: state.createSamlConnectionForm?.x509SignCert?.contents ?? '',
                    signInUrl: state.createSamlConnectionForm.signInUrl,
                    signOutUrl: state.createSamlConnectionForm.signOutUrl ?? undefined,
                }
            }
            break;
        }
        case PINGFEDERATE: {
            payload = {
                ...payload,
                pingFederateOptions: {
                    serverUrl: state.createPingConnectionForm.federateServerUrl,
                    signingCertificate: state.createPingConnectionForm?.x509SignCert?.contents
                }
            }
            break;
        }
        default: payload = { ...payload };
    }
    return payload;
}

export const STATUS_LABELS = {
    ESTABLISHED: "Established",
    AWAITING_APPROVAL: "Awaiting approval",
    APPROVED: 'Approved'
};

export const rsaKeyExpirationDate = (defaultCurrentDate = new Date(Date.now())) => {
    const utcDate = new Date(Date.UTC(
        defaultCurrentDate.getUTCFullYear(),
        defaultCurrentDate.getUTCMonth() + rsaExpirationMonths,
        defaultCurrentDate.getUTCDate()
    ));
    return utcDate;
}

export const isCertOrPublicKey = content => {
    const lines = content.trim().split('\n');
    const firstLine = lines[0].trim();
    if(firstLine.includes("BEGIN CERTIFICATE")){
        return "cert"
    }
    if(firstLine.includes("BEGIN PUBLIC KEY") || firstLine.includes("BEGIN RSA PUBLIC KEY")){
        return "publicKey"
    }
    else{
        return "invalid"
    }
} 

const extractCertExpiry = pemText => {
    try {
        // Remove the PEM header and footer, and decode the base64 content
        const b64 = pemText
            .replace(/-----BEGIN CERTIFICATE-----/, '')
            .replace(/-----END CERTIFICATE-----/, '')
            .replace(/\s+/g, '');
        
        // Decode the base64 string
        const binary = atob(b64);
        // Convert the binary string to a byte array
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        
        // Find all date fields (we need the second one - notAfter)
        const dates = [];
        
        for (let i = 0; i < bytes.length - 6; i++) {
            // Look for UTCTime (0x17) or GeneralizedTime (0x18) ASN.1 tag
            if (bytes[i] === 0x17 || bytes[i] === 0x18) {
                const len = bytes[i + 1];
                
                // Validate length to avoid reading beyond buffer
                if (i + 2 + len <= bytes.length && len > 0 && len <= 50) {
                    const dateStr = String.fromCharCode(...bytes.slice(i + 2, i + 2 + len));
                    
                    // Parse the date string
                    let parsedDate = null;
                    
                    if (bytes[i] === 0x18) {
                        // GeneralizedTime: YYYYMMDDHHMMSSZ or YYYYMMDDHHMMSS+HHMM
                        if (dateStr.length >= 14) {
                            const year = dateStr.slice(0, 4);
                            const month = dateStr.slice(4, 6);
                            const day = dateStr.slice(6, 8);
                            const hour = dateStr.slice(8, 10);
                            const minute = dateStr.slice(10, 12);
                            const second = dateStr.slice(12, 14);
                            
                            if (year >= '1950' && year <= '2099' && month >= '01' && month <= '12' && day >= '01' && day <= '31') {
                                parsedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
                            }
                        }
                    } else if (bytes[i] === 0x17) {
                        // UTCTime: YYMMDDHHMMSSZ or YYMMDDHHMMSS+HHMM
                        if (dateStr.length >= 12) {
                            let year = dateStr.slice(0, 2);
                            const month = dateStr.slice(2, 4);
                            const day = dateStr.slice(4, 6);
                            const hour = dateStr.slice(6, 8);
                            const minute = dateStr.slice(8, 10);
                            const second = dateStr.slice(10, 12);
                            
                            // Convert 2-digit year to 4-digit (RFC 5280: 50-99 = 1950-1999, 00-49 = 2000-2049)
                            year = (parseInt(year) >= 50) ? `19${year}` : `20${year}`;
                            
                            if (month >= '01' && month <= '12' && day >= '01' && day <= '31') {
                                parsedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
                            }
                        }
                    }
                    
                    if (parsedDate && !isNaN(parsedDate.getTime())) {
                        dates.push(parsedDate);
                    }
                }
            }
        }
        
        // Return the second date found (notAfter/expiration), or the last date if only one found
        if (dates.length >= 2) {
            return dates[1]; // notAfter (expiration date)
        } else if (dates.length === 1) {
            return dates[0]; // Fallback to the only date found
        }
        
        return undefined;
    } catch (error) {
        console.error('Error extracting certificate expiry:', error);
        return undefined;
    }
}

export const extractCertExpiration = pem => {
    try {
        return extractCertExpiry(pem);
    } catch (e) {
        return 'Unable to extract expiration date'
    }
}

export const formatDate = (isoString) => {
    const formattedDate = new Date(isoString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    return formattedDate;
}

export const isOrgInSafeMode = (orgTags) => {
    const safeModeTag = _.find(orgTags, tag => tag["internal:bxp:safeMode"]);
    const safeMode = safeModeTag ? safeModeTag["internal:bxp:safeMode"] : "false";
    return getBoolean(safeMode);
}

export const keyNameMapping = keyType => {
    const mapping = {
        "RSA_KEY": "RSA key",
        "X509_CERTIFICATE": "Public certificate",
    };
    return mapping[keyType] || keyType;
}

export const isDateInPast = (date) => {
    if (!date) return false;
    const dateToCheck = date instanceof Date ? date : new Date(date);
    if (isNaN(dateToCheck.getTime())) {
        return false;
    }
    const now = new Date();
    return dateToCheck < now;
};

export const getResourceTypeAndId = (resource) => {
    const resourceMap = {
        "internal:bxp:folderId": "folders",
        "internal:bxp:projectId": "projects",
        "internal:bxp:organizationId": "organizations"
    };

    const key = _.findKey(resourceMap, (type, key) => resource[key]);
    return key ? { type: resourceMap[key], id: resource[key] } : null;
};

export const formatUserGroupDetailsResponse = (bindingRes) => {
    const getId = obj => Object.values(obj)[0];
    const getHierarchyType = obj => {
        const key = Object.keys(obj)[0];
        if (key.includes("projectId")) return "project";
        if (key.includes("folderId")) return "folder";
        if (key.includes("organizationId")) return "organization";
        return null;
    };
    const bindingResMap = {};
    for (const binding of bindingRes) {
        const hierarchyId = getId(binding.resources[0]);
        const roleId = getId(binding.permissions[0]);
        if (!bindingResMap[hierarchyId]) {
            bindingResMap[hierarchyId] = {
                id: hierarchyId,
                resourceClass: "hierarchy",
                resourceType: getHierarchyType(binding.resources[0]),
                roles: [{ id: roleId }]
            };
        } else {
            const existingRolesForHeirarchy = bindingResMap[hierarchyId].roles;
            const isRoleAlreadyExists = existingRolesForHeirarchy.find(role => role.id === roleId);
            if (isRoleAlreadyExists) {
                continue;
            }
            bindingResMap[hierarchyId].roles.push({ id: roleId });
        }
    }
    return { items: Object.values(bindingResMap) };
}